import type { ReactNode } from 'react';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { EC, getNetChangeColor, formatNetValue } from '../../utils/economyStats';

/**
 * SF-164: `GroupCard`/`DataRow`/`T2Row`/`DeferredRow`/`CardSeparator`/`TableSeparator`/
 * `DataTriple`/`NetRow`, ported verbatim (JSX-only — the `EC` color constant this file used to
 * own moved to `economyStats.ts`, see that file's doc comment, mirroring `economyFlow.ts`'s
 * `SOURCE_COLORS`) out of the deleted `components/council/tabs/EconomyTab.tsx`. Consumed by
 * `DesktopEconomyShell` for the Population/Employment cards and the Net Changes table.
 */

export function GroupCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      className="rounded-lg overflow-hidden flex flex-col"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${EC.border}` }}
    >
      <div
        className="text-center text-[11px] font-bold uppercase tracking-widest px-3.5 py-2"
        style={{ backgroundColor: EC.background, color: EC.primary, borderBottom: `1px solid ${EC.border}` }}
      >
        {title}
      </div>
      <div className="px-3.5 py-2.5 flex-1">
        {children}
      </div>
    </div>
  );
}

/**
 * `label` widened from `string` to `ReactNode` (v1.3 fix) — zero cost, this is a brand-new file
 * with no existing consumers to break. Lets the Employment/Capacity row compose a colored status
 * word inline (e.g. `<>Employment (<span style={{color}}>{label}</span>)</>`) instead of
 * inheriting this row's hardcoded-tertiary label styling.
 */
export function DataRow({
  label,
  value,
  valueColor,
}: {
  label: ReactNode;
  value: ReactNode;
  valueColor?: string;
}) {
  return (
    <div className="flex justify-between items-center py-[3px] gap-3">
      <span className="text-sm" style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span className="text-sm font-mono" style={{ color: valueColor ?? COLORS.text.secondary }}>
        {value}
      </span>
    </div>
  );
}

export function T2Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between items-center py-[3px] gap-3">
      <span className="text-sm" style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span className="text-sm font-mono font-bold" style={{ color: COLORS.text.primary }}>
        {value}
      </span>
    </div>
  );
}

export function DeferredRow({ label }: { label: string }) {
  return (
    <div className="flex justify-between items-center py-[3px] gap-3">
      <span className="text-sm" style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span className="text-sm font-mono" style={{ color: COLORS.text.tertiary }}>—</span>
    </div>
  );
}

export function CardSeparator() {
  return <div className="my-1.5" style={{ borderTop: `1px solid ${EC.border}` }} />;
}

// ─── Net Changes Table Components ─────────────────────────────────────────────

export function TableSeparator() {
  return (
    <tr aria-hidden="true">
      <td
        colSpan={4}
        style={{ padding: '6px 0 4px', borderTop: `1px solid ${EC.border}` }}
      />
    </tr>
  );
}

export function DataTriple({
  label,
  yesterday,
  thisMonth,
  lastMonth,
}: {
  label: string;
  yesterday: number;
  thisMonth?: number | null;
  lastMonth?: number | null;
}) {
  return (
    <tr>
      <td className="py-[3px] text-sm" style={{ color: COLORS.text.secondary }}>{label}</td>
      <td className="text-right py-[3px] text-sm font-mono" style={{ color: COLORS.text.secondary }}>
        {formatNumber(Math.round(yesterday))}
      </td>
      <td
        className="text-right py-[3px] text-sm font-mono"
        style={{ color: thisMonth != null ? COLORS.text.secondary : COLORS.text.tertiary }}
      >
        {thisMonth != null ? formatNumber(Math.round(thisMonth)) : '—'}
      </td>
      <td
        className="text-right py-[3px] text-sm font-mono"
        style={{ color: lastMonth != null ? COLORS.text.secondary : COLORS.text.tertiary }}
      >
        {lastMonth != null ? formatNumber(Math.round(lastMonth)) : '—'}
      </td>
    </tr>
  );
}

export function NetRow({
  label,
  yesterday,
  thisMonth,
  lastMonth,
}: {
  label: string;
  yesterday: number;
  thisMonth?: number | null;
  lastMonth?: number | null;
}) {
  return (
    <tr style={{ backgroundColor: EC.dim }}>
      <td className="py-[5px] px-1 text-sm font-semibold text-game-200">{label}</td>
      <td
        className="text-right py-[5px] text-sm font-mono font-semibold"
        style={{ color: getNetChangeColor(yesterday) }}
      >
        {formatNetValue(yesterday)}
      </td>
      <td
        className="text-right py-[5px] text-sm font-mono font-semibold"
        style={{ color: thisMonth != null ? getNetChangeColor(thisMonth) : COLORS.text.tertiary }}
      >
        {thisMonth != null ? formatNetValue(thisMonth) : '—'}
      </td>
      <td
        className="text-right py-[5px] text-sm font-mono font-semibold"
        style={{ color: lastMonth != null ? getNetChangeColor(lastMonth) : COLORS.text.tertiary }}
      >
        {lastMonth != null ? formatNetValue(lastMonth) : '—'}
      </td>
    </tr>
  );
}
